import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import PolicyBalanceDialog from '../../components/Dialogs/PolicyBalanceDialog';
import { useClaimDetailDialog } from './ClaimDetailDialogContext';

interface PolicyBalanceDialogContextType {
  openDialog: (policyId: string, policyName?: string, policyNumber?: string) => void;
  closeDialog: () => void;
}

const PolicyBalanceDialogContext = createContext<PolicyBalanceDialogContextType | undefined>(undefined);

export const usePolicyBalanceDialog = (): PolicyBalanceDialogContextType => {
  const context = useContext(PolicyBalanceDialogContext);
  if (!context) {
    throw new Error('usePolicyBalanceDialog must be used within PolicyBalanceDialogProvider');
  }
  return context;
};

interface PolicyBalanceDialogProviderProps {
  children: ReactNode;
}

export const PolicyBalanceDialogProvider: React.FC<PolicyBalanceDialogProviderProps> = ({ children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [policyId, setPolicyId] = useState<string | null>(null);
  const [policyName, setPolicyName] = useState<string | undefined>(undefined);
  const [policyNumber, setPolicyNumber] = useState<string | undefined>(undefined);
  
  // Use the claim detail dialog hook here, where we have access to ClaimDetailDialogProvider
  const { openDialog: openClaimDetailDialog } = useClaimDetailDialog();

  const openDialog = useCallback((id: string, name?: string, number?: string) => {
    setPolicyId(id);
    setPolicyName(name);
    setPolicyNumber(number);
    setIsVisible(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsVisible(false);
    setPolicyId(null);
    setPolicyName(undefined);
    setPolicyNumber(undefined);
  }, []);

  return (
    <PolicyBalanceDialogContext.Provider value={{ openDialog, closeDialog }}>
      {children}
      <PolicyBalanceDialog
        visible={isVisible}
        policyId={policyId}
        policyName={policyName}
        policyNumber={policyNumber}
        onClose={closeDialog}
        onOpenClaimDetail={openClaimDetailDialog}
      />
    </PolicyBalanceDialogContext.Provider>
  );
};

